import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { getLevelInfo, getGrade } from '../utils/xpUtils';
import Icon from '../components/Icon';

const EMOJI_PRESETS = ['🏎️','🏆','🔥','⚡','🚀','🏁','🎯','👑','🦅','🐉','🛡️','⭐'];
const COLOR_PRESETS = ['#e10600','#3671C6','#FF8000','#27F4D2','#229971','#FF87BC','#facc15','#a855f7','#64C4FF','#52E252'];

/* ── Utilities ──────────────────────────────────────────────────────────── */
function copyToClipboard(text) {
  navigator.clipboard?.writeText(text).catch(() => {});
}

/* ── CreateModal ─────────────────────────────────────────────────────────── */
const CreateModal = ({ onClose, onCreated }) => {
  const [name, setName]         = useState('');
  const [tag, setTag]           = useState('');
  const [desc, setDesc]         = useState('');
  const [emoji, setEmoji]       = useState('🏎️');
  const [color, setColor]       = useState('#e10600');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !tag.trim()) return;
    setLoading(true); setError('');
    try {
      const res = await api.post('/ecuries', { name: name.trim(), tag: tag.trim(), description: desc.trim(), emoji, color });
      onCreated(res.data);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || 'Erreur');
    } finally { setLoading(false); }
  };

  return (
    <>
      <style>{`
        @keyframes em-backdrop{from{opacity:0}to{opacity:1}}
        @keyframes em-card{0%{transform:scale(0.5) translateY(40px);opacity:0}65%{transform:scale(1.04) translateY(-4px);opacity:1}82%{transform:scale(0.98) translateY(2px)}100%{transform:scale(1) translateY(0);opacity:1}}
        @keyframes em-stripe{0%{transform:scaleX(0)}100%{transform:scaleX(1)}}
        @keyframes em-in{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
      `}</style>
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4"
        style={{ animation:'em-backdrop 0.25s ease forwards', background:'rgba(0,0,0,0.90)', backdropFilter:'blur(10px)' }}
        onClick={onClose}>
        <div className="relative w-full max-w-sm rounded-2xl overflow-hidden"
          style={{
            border:`2px solid ${color}`,
            background:'linear-gradient(160deg,#0c0c10 0%,#e1060012 100%)',
            boxShadow:`0 0 50px ${color}40,0 0 100px ${color}18,inset 0 0 30px ${color}06`,
            animation:'em-card 0.6s cubic-bezier(0.34,1.56,0.64,1) 0.05s both',
          }}
          onClick={e => e.stopPropagation()}>

          <div style={{ height:4, background:`linear-gradient(90deg,${color} 0%,transparent 80%)`, animation:'em-stripe 0.5s 0.15s ease-out both', transformOrigin:'left' }} />

          <form onSubmit={handleSubmit} className="px-5 py-5 flex flex-col gap-4"
            style={{ animation:'em-in 0.35s 0.3s ease both', opacity:0 }}>
            <div className="flex items-center justify-between">
              <h2 className="text-white font-black text-lg">Créer une écurie</h2>
              <button type="button" onClick={onClose}
                className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{ background:'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.1)' }}>
                <span className="text-zinc-400 text-sm">✕</span>
              </button>
            </div>

            {/* Emoji + Color row */}
            <div className="flex gap-3 items-start">
              <div className="flex flex-col gap-1.5">
                <p className="text-zinc-500 text-[10px] uppercase tracking-wider">Emoji</p>
                <div className="flex flex-wrap gap-1 w-32">
                  {EMOJI_PRESETS.map(e => (
                    <button key={e} type="button" onClick={() => setEmoji(e)}
                      className={`w-7 h-7 text-base rounded-lg flex items-center justify-center transition-all ${emoji === e ? 'ring-2 ring-white scale-110' : 'hover:scale-105'}`}
                      style={{ background: emoji === e ? `${color}30` : 'rgba(255,255,255,0.05)' }}>
                      {e}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-1.5 flex-1">
                <p className="text-zinc-500 text-[10px] uppercase tracking-wider">Couleur</p>
                <div className="flex flex-wrap gap-1.5">
                  {COLOR_PRESETS.map(c => (
                    <button key={c} type="button" onClick={() => setColor(c)}
                      className={`w-6 h-6 rounded-full transition-all ${color === c ? 'ring-2 ring-white scale-110' : 'hover:scale-105'}`}
                      style={{ background: c }} />
                  ))}
                </div>
              </div>
            </div>

            {/* Name */}
            <div>
              <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1">Nom de l'écurie</p>
              <input value={name} onChange={e => setName(e.target.value)} maxLength={30}
                placeholder="ex: Les Scuderia du Nord"
                className="w-full rounded-xl px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none"
                style={{ background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)' }}
                onFocus={e => { e.target.style.borderColor = color; }}
                onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; }} />
            </div>

            {/* Tag */}
            <div>
              <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1">Tag (2–4 lettres)</p>
              <input value={tag} onChange={e => setTag(e.target.value.toUpperCase().slice(0,4))} maxLength={4}
                placeholder="ex: SCU"
                className="w-full rounded-xl px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none font-black tracking-widest"
                style={{ background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)' }}
                onFocus={e => { e.target.style.borderColor = color; }}
                onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; }} />
            </div>

            {/* Description */}
            <div>
              <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1">Description (optionnel)</p>
              <textarea value={desc} onChange={e => setDesc(e.target.value)} maxLength={200} rows={2}
                placeholder="Quelques mots sur votre écurie…"
                className="w-full rounded-xl px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none resize-none"
                style={{ background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)' }}
                onFocus={e => { e.target.style.borderColor = color; }}
                onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; }} />
            </div>

            {error && <p className="text-red-400 text-xs">{error}</p>}

            <button type="submit" disabled={!name.trim() || !tag.trim() || loading}
              className="w-full py-3 rounded-xl font-black text-sm text-white disabled:opacity-40 transition-all"
              style={{ background: color, boxShadow:`0 4px 20px ${color}50` }}>
              {loading ? 'Création…' : `${emoji} Créer l'écurie`}
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

/* ── DetailModal ─────────────────────────────────────────────────────────── */
const DetailModal = ({ ecurieId, onClose, user, onUpdate }) => {
  const [ecurie, setEcurie]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [actLoading, setActLoading] = useState(false);
  const [copied, setCopied]     = useState(false);
  const [error, setError]       = useState('');

  const fetch = useCallback(() => {
    setLoading(true);
    api.get(`/ecuries/${ecurieId}`)
      .then(r => setEcurie(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [ecurieId]);

  useEffect(() => { fetch(); }, [fetch]);

  const isMember  = ecurie && user && ecurie.members.some(m => (m.user?._id ?? m.user) === user.id || (m.user?.username === user.username));
  const isOwner   = ecurie && user && ecurie.owner?._id === user.id;

  const handleJoin = async () => {
    setActLoading(true); setError('');
    try {
      await api.post(`/ecuries/${ecurieId}/join`);
      fetch(); onUpdate?.();
    } catch (err) { setError(err?.response?.data?.message || 'Erreur'); }
    finally { setActLoading(false); }
  };

  const handleLeave = async () => {
    setActLoading(true); setError('');
    try {
      await api.post(`/ecuries/${ecurieId}/leave`);
      onClose(); onUpdate?.();
    } catch (err) { setError(err?.response?.data?.message || 'Erreur'); }
    finally { setActLoading(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer définitivement cette écurie ?')) return;
    setActLoading(true);
    try {
      await api.delete(`/ecuries/${ecurieId}`);
      onClose(); onUpdate?.();
    } catch (err) { setError(err?.response?.data?.message || 'Erreur'); }
    finally { setActLoading(false); }
  };

  const color = ecurie?.color ?? '#e10600';

  return (
    <>
      <style>{`
        @keyframes dm-backdrop{from{opacity:0}to{opacity:1}}
        @keyframes dm-card{0%{transform:scale(0.5) translateY(40px);opacity:0}65%{transform:scale(1.04) translateY(-4px);opacity:1}82%{transform:scale(0.98) translateY(2px)}100%{transform:scale(1) translateY(0);opacity:1}}
        @keyframes dm-stripe{0%{transform:scaleX(0)}100%{transform:scaleX(1)}}
        @keyframes dm-in{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
      `}</style>
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4"
        style={{ animation:'dm-backdrop 0.25s ease forwards', background:'rgba(0,0,0,0.92)', backdropFilter:'blur(12px)' }}
        onClick={onClose}>
        <div className="relative w-full max-w-md rounded-2xl overflow-hidden flex flex-col"
          style={{
            border:`2px solid ${color}`,
            background:'linear-gradient(160deg,#0c0c10 0%,#0c0c10ee 100%)',
            boxShadow:`0 0 50px ${color}40,0 0 100px ${color}18`,
            animation:'dm-card 0.6s cubic-bezier(0.34,1.56,0.64,1) 0.05s both',
            maxHeight:'88vh',
          }}
          onClick={e => e.stopPropagation()}>

          <div style={{ height:4, background:`linear-gradient(90deg,${color} 0%,transparent 80%)`, animation:'dm-stripe 0.5s 0.15s ease-out both', transformOrigin:'left', flexShrink:0 }} />

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor:`${color} transparent transparent transparent` }} />
            </div>
          ) : !ecurie ? (
            <p className="text-zinc-500 text-sm text-center py-12">Écurie introuvable.</p>
          ) : (
            <>
              {/* Header */}
              <div className="px-5 pt-5 pb-4 shrink-0" style={{ animation:'dm-in 0.35s 0.3s ease both', opacity:0 }}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl shrink-0"
                      style={{ background:`${color}20`, border:`2px solid ${color}50` }}>
                      {ecurie.emoji}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="text-white font-black text-xl leading-tight">{ecurie.name}</h2>
                        <span className="text-[10px] font-black px-1.5 py-0.5 rounded font-mono"
                          style={{ color, background:`${color}20`, border:`1px solid ${color}40` }}>
                          [{ecurie.tag}]
                        </span>
                      </div>
                      <p className="text-zinc-500 text-xs mt-0.5">{ecurie.members.length} membre{ecurie.members.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                    style={{ background:'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.1)' }}>
                    <span className="text-zinc-400 text-sm">✕</span>
                  </button>
                </div>

                {ecurie.description && (
                  <p className="text-zinc-400 text-xs mt-3 leading-relaxed">{ecurie.description}</p>
                )}

                {/* Invite code */}
                <div className="mt-3 flex items-center gap-2 rounded-xl px-3 py-2"
                  style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)' }}>
                  <span className="text-zinc-500 text-[10px] uppercase tracking-wider shrink-0">Code :</span>
                  <span className="text-white font-black text-sm tracking-[0.2em] font-mono flex-1">{ecurie.inviteCode}</span>
                  <button onClick={() => { copyToClipboard(ecurie.inviteCode); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
                    className="text-[10px] font-bold px-2 py-0.5 rounded transition-colors"
                    style={{ color, background:`${color}18` }}>
                    {copied ? '✓ Copié' : 'Copier'}
                  </button>
                </div>
              </div>

              <div className="h-px mx-5" style={{ background:`linear-gradient(90deg,${color} 0%,transparent 80%)`, opacity:0.25 }} />

              {/* Leaderboard */}
              <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-2"
                style={{ animation:'dm-in 0.35s 0.45s ease both', opacity:0 }}>
                <p className="text-[10px] font-black uppercase tracking-[0.25em] mb-1" style={{ color }}>Classement interne</p>
                {ecurie.members.map((m, i) => {
                  const u = m.user;
                  if (!u) return null;
                  const { level } = getLevelInfo(u.xp ?? 0);
                  const g = getGrade(level);
                  const isOwnerRow = (u._id ?? u) === (ecurie.owner?._id ?? ecurie.owner) || u.username === ecurie.owner?.username;
                  return (
                    <Link key={u._id ?? i} to={`/joueurs/${u._id}`} onClick={onClose}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all hover:opacity-80 no-underline"
                      style={{
                        borderColor: i === 0 ? `${color}50` : 'rgba(255,255,255,0.06)',
                        background:  i === 0 ? `${color}0d` : 'rgba(255,255,255,0.02)',
                      }}>
                      <span className="text-sm font-black w-6 text-center shrink-0"
                        style={{ color: i === 0 ? '#F59E0B' : i === 1 ? '#94A3B8' : i === 2 ? '#B45309' : '#52525b' }}>
                        {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i+1}`}
                      </span>
                      <div className="w-8 h-8 rounded-full overflow-hidden border shrink-0"
                        style={{ borderColor:`${color}40` }}>
                        {u.avatar
                          ? <img src={u.avatar} alt="" className="w-full h-full object-cover" />
                          : <div className="w-full h-full flex items-center justify-center text-xs font-black"
                              style={{ background:`${color}22`, color }}>
                              {u.username?.[0]?.toUpperCase()}
                            </div>
                        }
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="text-white text-xs font-bold truncate">{u.username}</p>
                          {isOwnerRow && <Icon name="star" size={9} />}
                        </div>
                        <span className="text-[9px] font-black uppercase tracking-wider px-1 py-0.5 rounded"
                          style={{ color: g.color, background: g.bg }}>{g.label}</span>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-white font-black text-sm">{m.totalScore ?? 0}</p>
                        <p className="text-zinc-600 text-[9px] uppercase">pts</p>
                      </div>
                    </Link>
                  );
                })}
              </div>

              {/* Actions */}
              {error && <p className="text-red-400 text-xs px-5 pb-1">{error}</p>}
              {user && (
                <div className="px-5 py-4 shrink-0 flex gap-2 border-t border-white/5">
                  {!isMember && !isOwner && (
                    <button onClick={handleJoin} disabled={actLoading}
                      className="flex-1 py-2.5 rounded-xl font-black text-sm text-white disabled:opacity-40"
                      style={{ background: color, boxShadow:`0 4px 16px ${color}40` }}>
                      {actLoading ? '…' : 'Rejoindre l\'écurie'}
                    </button>
                  )}
                  {isMember && !isOwner && (
                    <button onClick={handleLeave} disabled={actLoading}
                      className="flex-1 py-2.5 rounded-xl font-black text-sm text-zinc-300 border border-zinc-700 hover:border-red-500/50 hover:text-red-400 transition-colors disabled:opacity-40">
                      {actLoading ? '…' : 'Quitter l\'écurie'}
                    </button>
                  )}
                  {isOwner && (
                    <button onClick={handleDelete} disabled={actLoading}
                      className="flex-1 py-2.5 rounded-xl font-black text-sm text-red-400 border border-red-900/40 hover:bg-red-900/20 transition-colors disabled:opacity-40">
                      {actLoading ? '…' : '🗑 Supprimer l\'écurie'}
                    </button>
                  )}
                </div>
              )}
              {!user && (
                <div className="px-5 py-3 text-center shrink-0 border-t border-white/5">
                  <Link to="/login" onClick={onClose} className="text-xs text-zinc-400 hover:text-white">
                    <span style={{ color }}>Connecte-toi</span> pour rejoindre cette écurie
                  </Link>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

/* ── EcurieCard ──────────────────────────────────────────────────────────── */
const EcurieCard = ({ ecurie, onClick, isMyEcurie }) => {
  const color = ecurie.color ?? '#e10600';
  return (
    <button onClick={onClick}
      className="group relative overflow-hidden rounded-2xl border text-left transition-all duration-200 hover:-translate-y-0.5 w-full"
      style={{
        borderColor: `${color}40`,
        background: `linear-gradient(135deg,${color}0d 0%,#09090b 60%)`,
        boxShadow: isMyEcurie ? `0 0 18px ${color}20` : undefined,
      }}>
      <div className="h-0.5 w-full" style={{ background:`linear-gradient(90deg,${color} 0%,transparent 70%)` }} />
      <div className="p-4 flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center text-xl shrink-0"
          style={{ background:`${color}18`, border:`1.5px solid ${color}40` }}>
          {ecurie.emoji}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-white font-black text-sm truncate">{ecurie.name}</p>
            <span className="text-[9px] font-black px-1 py-0.5 rounded font-mono shrink-0"
              style={{ color, background:`${color}18` }}>[{ecurie.tag}]</span>
          </div>
          <p className="text-zinc-500 text-[10px] mt-0.5 truncate">
            {ecurie.owner?.username && <span className="text-zinc-400">par {ecurie.owner.username} · </span>}
            {ecurie.memberCount ?? ecurie.members?.length ?? 0} membre{(ecurie.memberCount ?? ecurie.members?.length ?? 0) !== 1 ? 's' : ''}
          </p>
          {ecurie.description && (
            <p className="text-zinc-600 text-[10px] mt-1 truncate">{ecurie.description}</p>
          )}
        </div>
        <span className="text-zinc-600 group-hover:text-zinc-400 text-sm transition-colors shrink-0">→</span>
      </div>
    </button>
  );
};

/* ── Main page ───────────────────────────────────────────────────────────── */
const Ecuries = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [all, setAll]             = useState([]);
  const [mine, setMine]           = useState([]);
  const [search, setSearch]       = useState('');
  const [loading, setLoading]     = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [allRes, mineRes] = await Promise.all([
        api.get('/ecuries'),
        user ? api.get('/ecuries/mine') : Promise.resolve({ data: [] }),
      ]);
      setAll(allRes.data);
      setMine(mineRes.data);
    } catch {} finally { setLoading(false); }
  }, [user]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = all.filter(e =>
    e.name.toLowerCase().includes(search.toLowerCase()) ||
    e.tag.toLowerCase().includes(search.toLowerCase())
  );

  const myIds = new Set(mine.map(e => e._id));

  return (
    <div className="max-w-4xl mx-auto px-4 py-10">
      {/* Header */}
      <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-zinc-500 text-xs uppercase tracking-widest font-medium mb-1">Communauté</p>
          <h1 className="text-4xl font-black text-white tracking-tight">
            Les <span className="text-f1red">Écuries</span>
          </h1>
          <p className="text-zinc-400 mt-2 text-sm max-w-md">
            Crée ou rejoins une écurie, compare tes scores avec tes amis et construis ta réputation.
          </p>
        </div>
        {user && (
          <button onClick={() => setShowCreate(true)}
            className="shrink-0 px-5 py-2.5 rounded-xl font-black text-sm text-white transition-all hover:-translate-y-0.5"
            style={{ background:'#e10600', boxShadow:'0 4px 20px #e1060050' }}>
            + Créer une écurie
          </button>
        )}
      </div>

      {/* Mes écuries */}
      {user && mine.length > 0 && (
        <div className="mb-10">
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-f1red mb-3">Mes écuries ({mine.length}/5)</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {mine.map(e => (
              <EcurieCard key={e._id} ecurie={e} onClick={() => navigate(`/ecuries/${e._id}`)} isMyEcurie />
            ))}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative mb-6">
        <svg className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
        </svg>
        <input type="text" placeholder="Rechercher une écurie…" value={search} onChange={e => setSearch(e.target.value)}
          className="w-full rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder-zinc-500 focus:outline-none transition-all"
          style={{ background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.10)' }}
          onFocus={e => { e.target.style.borderColor='#e10600'; e.target.style.boxShadow='0 0 0 3px #e1060018'; }}
          onBlur={e => { e.target.style.borderColor='rgba(255,255,255,0.10)'; e.target.style.boxShadow='none'; }}
        />
        {search && (
          <button onClick={() => setSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors text-lg">✕</button>
        )}
      </div>

      {/* All écuries */}
      {loading ? (
        <div className="flex flex-col items-center justify-center h-40 gap-3">
          <div className="w-8 h-8 border-2 border-f1red border-t-transparent rounded-full animate-spin" />
          <p className="text-zinc-500 text-sm">Chargement des écuries…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="card text-center py-14">
          {search
            ? <><p className="text-zinc-400 font-bold mb-1">Aucune écurie trouvée</p><p className="text-zinc-600 text-sm">Essaie un autre terme.</p></>
            : <><p className="text-zinc-400 font-bold mb-2">Aucune écurie pour l'instant.</p>
               {user
                 ? <button onClick={() => setShowCreate(true)} className="text-f1red text-sm hover:underline font-bold">Sois le premier à en créer une →</button>
                 : <Link to="/login" className="text-f1red text-sm hover:underline font-bold">Connecte-toi pour créer la première →</Link>
               }</>
          }
        </div>
      ) : (
        <>
          {!search && <p className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-500 mb-3">Toutes les écuries ({all.length})</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {filtered.map(e => (
              <EcurieCard key={e._id} ecurie={e} onClick={() => navigate(`/ecuries/${e._id}`)} isMyEcurie={myIds.has(e._id)} />
            ))}
          </div>
        </>
      )}

      {/* Modals */}
      {showCreate && (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onCreated={() => fetchAll()}
        />
      )}
    </div>
  );
};

export default Ecuries;
